import { useCallback, useEffect, useState } from "react";

import {
  DEFAULT_SECTION,
  findSection,
  groupSections,
  indexInGroup,
  nextSection,
  prevSection,
  resolveSection,
} from "./editSections";

/* Where the viewer is in the flow, and how they move. */

export const CURRENT_KEY = "CurrentComponent";
export const INDEX_KEY = "accordianIndex";

const readSaved = () => {
  try {
    const saved = localStorage.getItem(CURRENT_KEY);
    /* Transcribed: the live restore treats the STRING "null" as missing, which
       is what gets stored when a null label is written. */
    if (!saved || saved === "null") return null;
    return saved;
  } catch {
    /* Storage can throw outright in a private window. The flow still has to
       open, at its first section. */
    return null;
  }
};

const persist = (label) => {
  try {
    localStorage.setItem(CURRENT_KEY, label);
    localStorage.setItem(INDEX_KEY, String(indexInGroup(label)));
  } catch {
    /* Unwritable storage costs the resume, not the session. */
  }
};

export const useEditNavigation = ({ onAdvance, onVisit } = {}) => {
  /* Resolved on the first render rather than in an effect, so nothing renders
     the wrong section for a frame. */
  const [section, setSection] = useState(() => resolveSection(readSaved() || DEFAULT_SECTION));
  const [openGroup, setOpenGroup] = useState(
    () => findSection(resolveSection(readSaved() || DEFAULT_SECTION))?.group || "About"
  );

  /* Transcribed: the live page seeds these keys on mount when nothing is
     stored yet. */
  useEffect(() => {
    if (!readSaved()) persist(DEFAULT_SECTION);
  }, []);

  const goTo = useCallback(
    (label, { advance = false } = {}) => {
      const target = findSection(label);
      /* An unknown label is ignored rather than blanking the panel — the live
         `getCurrentComponent` also does nothing when the lookup misses, which
         is precisely why its typos are invisible. */
      if (!target) return;

      setSection(target.label);
      setOpenGroup(target.group);
      persist(target.label);

      /* Transcribed: Continue closes the outstanding admin request before
         moving on; Skip and Back do not. Both then refetch the company. */
      if (advance) onAdvance?.();
      onVisit?.();
    },
    [onAdvance, onVisit]
  );

  /* Transcribed from toggleAccordion: opening a group jumps to its first
     section, and clicking the open group collapses it without moving. */
  const toggleGroup = useCallback(
    (groupId) => {
      /* Collapsing: the section on screen STAYS on screen. That is the live
         behaviour — toggleAccordion returns null for the open group and
         touches neither currentComponent nor stepIndex. */
      if (openGroup === groupId) {
        setOpenGroup(null);
        return;
      }

      setOpenGroup(groupId);

      const first = groupSections(groupId)[0];
      if (first) {
        setSection(first.label);
        persist(first.label);
        onVisit?.();
      }
    },
    [openGroup, onVisit]
  );

  const next = useCallback(() => {
    const target = nextSection(section);
    if (target) goTo(target.label, { advance: true });
  }, [section, goTo]);

  const skip = useCallback(() => {
    const target = nextSection(section);
    if (target) goTo(target.label);
  }, [section, goTo]);

  const back = useCallback(() => {
    const target = prevSection(section);
    if (target) goTo(target.label);
  }, [section, goTo]);

  return {
    section,
    openGroup,
    goTo,
    toggleGroup,
    next,
    skip,
    back,
    /* Whether a Back button belongs on this section at all. */
    hasPrev: Boolean(prevSection(section)),
    /* And whether anything follows it — the last section ends the flow, so it
       says "Finish and Submit" rather than promising a next step. */
    hasNext: Boolean(nextSection(section)),
  };
};

export default useEditNavigation;
